import { useState, useEffect } from 'react';
import { useGeocoder } from '../services/GeocoderGoogleMaps';
import { useSelector } from 'react-redux';
import { getCurrentUserLocationUnitsStr, getHorseListingLocationUnitsStr } from '../util/location';
import { excludeTypes } from '../util/googleMaps';
import { checkUserPredictionData, setUserPredictionData } from '../util/localStorage';
import { isLocalhost, removeCountriesFromSearchString } from '../util/urlHelpers';
import config from '../config';

const { userTypeHorseowner } = config;

export const useCurrentUserGeoData = ({ allowed } = { allowed: true }) => {
    const geocoder = useGeocoder();

    const currentUser = useSelector(s => s.user.currentUser);
    const ownEntities = useSelector(s => s.ManageListingsPage.ownEntities?.ownListing);
    const queryInProgress = useSelector(s => s.ManageListingsPage.queryInProgress);
    const currentUseId = currentUser?.id?.uuid;
    const { userType, mainHorseId } = currentUser?.attributes?.profile?.publicData || {};
    const isHorseowner = userType === userTypeHorseowner;

    const ownEntitiesRequestInProgress = queryInProgress && isHorseowner;

    const [loading, setLoading] = useState(false);
    const [geoData, setGeoData] = useState(null);

    const getGeoData = async () => {
        try {
            setLoading(true);

            const getEntityLocationUnitsStr = () => {
                if (isHorseowner && ownEntities && ownEntities[mainHorseId]) {
                    return getHorseListingLocationUnitsStr(ownEntities[mainHorseId]);
                }
                return getCurrentUserLocationUnitsStr(currentUser);
            };

            const entityLocationSearchString = getEntityLocationUnitsStr();

            const geoData = await geocoder.getPlaceDetailsFromString({
                locationUnitsStr: entityLocationSearchString,
                excludeTypes,
            });

            setGeoData({
                ...geoData,
                entityLocationSearchString: removeCountriesFromSearchString(
                    entityLocationSearchString
                ),
            });

            /**
             * prediction data has to be already set to local storage
             * after successful getPlacePredictions call
             */
            const userPredictionData = checkUserPredictionData(entityLocationSearchString);

            if (userPredictionData && userPredictionData.data) {
                /**
                 * Re-set the local storage values with an extra params;
                 * Here we want the current location to be stored in
                 * the storage permanently, so the timestamp is set to Infinity
                 */
                /**
                 * local storage value is stringified, pure Infinity is converted to null;
                 * in order to avoid it, the Infinity is converted to a string 'Infinity'
                 */
                setUserPredictionData(entityLocationSearchString, userPredictionData.data, {
                    timestamp: 'Infinity',
                });
            }
        } catch (e) {
            if (isLocalhost()) {
                console.log('useCurrentUserGeoData.error:' + e.message);
            }
            return setGeoData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!allowed) return;
        if (!currentUseId) return;
        if (ownEntitiesRequestInProgress) return;

        getGeoData();
    }, [allowed, currentUseId, ownEntitiesRequestInProgress]);

    return { loading, geoData };
};
